import type { ManualClPoolStore } from "@/domains/thick-v1/manual-cl-store"

/**
 * Shared tooltip body for the "7D range APR" badge. Used from both the
 * pool-list row and the pool-detail page so the phrasing, source labels,
 * and baseline comparison stay identical across surfaces.
 *
 * The body explains where the number comes from (`seven-day-history`
 * vs `fallback-pct` vs `unavailable`), shows the capital-efficiency
 * multiplier, the 7-day full-range baseline, and the familiar 24h pool
 * APR for context, and warns the user when price is currently outside
 * the 7-day range so the headline number cannot be earned right now.
 */
export function RangeAprTooltipBody({
  rangeAprResult,
  fullRangeApr,
  formattedBaselinePct = rangeAprResult.formattedBaselinePct,
}: {
  rangeAprResult: ManualClPoolStore["rangeApr7d"]
  fullRangeApr: string
  formattedBaselinePct?: string
}) {
  const { source, sourceWindow, sourceLabel, capitalEfficiency, inRange } = rangeAprResult

  return (
    <div>
      <div className="font-medium text-t100">7D range APR</div>
      <div className="mt-1 leading-snug text-t200">{sourceLabel}</div>
      {source !== "unavailable" && (
        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] leading-snug text-t200">
          {capitalEfficiency > 0 && Number.isFinite(capitalEfficiency) && (
            <>
              <dt className="text-t300">Capital efficiency</dt>
              <dd className="text-right text-t100">{capitalEfficiency.toFixed(1)}×</dd>
            </>
          )}
          {formattedBaselinePct && (
            <>
              <dt className="text-t300">Full-range baseline ({sourceWindow})</dt>
              <dd className="text-right text-t100">{formattedBaselinePct}</dd>
            </>
          )}
          {fullRangeApr && (
            <>
              <dt className="text-t300">Pool APR (24h)</dt>
              <dd className="text-right text-t100">{fullRangeApr}</dd>
            </>
          )}
        </dl>
      )}
      {!inRange && source !== "unavailable" && (
        <div className="mt-2 text-[11px] leading-snug text-alert-300">
          Current price is outside the 7-day range — a position sized to this range would not be earning right now.
        </div>
      )}
    </div>
  )
}
